import React from 'react'
import classNames from 'classnames'
import { TableRow, TableCell } from 'transfix-ui/core'
import makeStyles from 'transfix-ui/core/styles/makeStyles'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { RightArrowIcon } from 'transfix-icons'
import { IPriceRequestDetailsFragment } from '../../graphql/PriceRequestDetailsFragment'
import { formatExternalId, formatCreatedAtTime } from '../utils/formatHistoricalQuotesTableContents'
import { BidStatus } from './BidStatus'
import { EquipmentIconCell } from './EquipmentIconCell'
import { LocationTimeCell } from './LocationTimeCell'
import { RequestedByAvatar } from './RequestedByAvatar'
import { SubmittedQuoteCell } from './SubmittedQuoteCell'

interface IHistoricalQuotesRowProps {
  priceRequest: IPriceRequestDetailsFragment
  focusedQuoteId: string | null
  setFocusedQuoteId: React.Dispatch<React.SetStateAction<string | null>>
}

const useStyles = makeStyles(theme => ({
  createdAt: {
    marginRight: theme.spacing(0.5),
  },
  selectedCell: {
    borderTop: '2px solid black',
    borderBottom: '2px solid black !important',
  },
  startCell: {
    borderLeft: '2px solid black',
    borderRadius: '5px 0px 0px 5px',
  },
  endCell: { borderRight: '2px solid black', borderRadius: '0px 5px 5px 0px' },
  textWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
}))

export function HistoricalQuotesRow({
  focusedQuoteId,
  priceRequest,
  setFocusedQuoteId,
}: IHistoricalQuotesRowProps) {
  const classes = useStyles()
  const sqtObjectiveFourEnabled = useFeatureFlag('sqtObjectiveFour')
  const isQuoteFocused = focusedQuoteId === priceRequest.id

  const selectedStartCell = classNames({
    [classes.selectedCell]: isQuoteFocused,
    [classes.startCell]: isQuoteFocused,
  })
  const selectedEndCell = classNames({
    [classes.selectedCell]: isQuoteFocused,
    [classes.endCell]: isQuoteFocused,
  })
  const selectedCell = classNames({ [classes.selectedCell]: isQuoteFocused })

  const openPanelHandler = () => {
    setFocusedQuoteId(priceRequest.id)
  }
  if (focusedQuoteId === null) {
    return (
      <TableRow>
        <TableCell>
          <RequestedByAvatar
            firstName={priceRequest.requestedBy?.firstName}
            lastName={priceRequest.requestedBy?.lastName}
            userType={priceRequest.requestedBy?.userType}
          />
          {formatCreatedAtTime(priceRequest.createdAt)}
        </TableCell>
        <TableCell>
          {priceRequest.id} {priceRequest.isTestQuote && <span className='tab-counter'>Test</span>}
        </TableCell>
        <TableCell>{priceRequest.shipperApplication?.name}</TableCell>
        <TableCell>
          <div title={typeof priceRequest.externalId === 'string' ? priceRequest.externalId : ''}>
            {formatExternalId(priceRequest.externalId)}
          </div>
        </TableCell>
        <TableCell>
          <div className={classes.textWrapper}>
            <EquipmentIconCell
              equipmentId={priceRequest.equipmentId}
              showLabel={!sqtObjectiveFourEnabled}
            />
            {sqtObjectiveFourEnabled &&
              `${priceRequest.pickupDropTrailer ? 'Drop' : 'Live'} - ${
                priceRequest.deliveryDropTrailer ? 'Drop' : 'Live'
              }`}
          </div>
        </TableCell>
        <LocationTimeCell
          city={priceRequest.pickupCity}
          stateCode={priceRequest.pickupStateCode}
          zip={priceRequest.pickupZipcode}
          date={priceRequest.pickupStartDate}
          time={priceRequest.pickupStartTime}
          additionalStops={priceRequest.additionalStops}
        />
        <LocationTimeCell
          city={priceRequest.deliveryCity}
          stateCode={priceRequest.deliveryStateCode}
          zip={priceRequest.deliveryZipcode}
          date={priceRequest.deliveryStartDate}
          time={priceRequest.deliveryStartTime}
        />
        <TableCell>
          <SubmittedQuoteCell
            customerQuote={priceRequest.customerQuote}
            allInQuote={priceRequest.premiums?.quoteWithFuel}
            linehaulQuote={priceRequest.premiums?.quoteWithoutFuel}
            customerQuoteType={priceRequest.customerQuoteType}
            isDoNotBidApplied={priceRequest.isDoNotBidApplied}
          />
        </TableCell>
        <TableCell>
          <BidStatus status={null} />
        </TableCell>
        <TableCell>
          <span
            aria-label='Open Panel'
            className='pointer icon-hoverable'
            role='button'
            tabIndex={0}
            onClick={openPanelHandler}
            onKeyPress={openPanelHandler}
            data-test={`expandable-toggle-${priceRequest.id}`}>
            <RightArrowIcon className='icon large text-secondary' />
          </span>
        </TableCell>
      </TableRow>
    )
  }
  return (
    <TableRow onClick={openPanelHandler}>
      <TableCell className={selectedStartCell}>
        <RequestedByAvatar
          firstName={priceRequest.requestedBy?.firstName}
          lastName={priceRequest.requestedBy?.lastName}
          userType={priceRequest.requestedBy?.userType}
        />
      </TableCell>
      <TableCell className={selectedCell}>{priceRequest.shipperApplication?.name}</TableCell>
      <LocationTimeCell
        className={selectedCell}
        city={priceRequest.pickupCity}
        stateCode={priceRequest.pickupStateCode}
        zip={priceRequest.pickupZipcode}
        date={priceRequest.pickupStartDate}
        time={priceRequest.pickupStartTime}
        additionalStops={priceRequest.additionalStops}
      />
      <LocationTimeCell
        className={selectedEndCell}
        city={priceRequest.deliveryCity}
        stateCode={priceRequest.deliveryStateCode}
        zip={priceRequest.deliveryZipcode}
        date={priceRequest.deliveryStartDate}
        time={priceRequest.deliveryStartTime}
      />
    </TableRow>
  )
}
